import { TFunction } from 'i18next'
import { useCallback, useEffect, useState } from 'react'

import { awaitPendingJob, displayErrorToast, premiumColors } from '.'
import { getEntityFromBlizzard } from '../api/search'
import { toast } from 'sonner'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import BnetLogoWhite from 'public/images/bnet-logo-white.svg?react'
import BnetLogoBlack from 'public/images/bnet-logo.svg?react'
import { useTheme } from '@/components/theme-provider'
import dayjs from 'dayjs'
import { useUserTeamAccess } from '../config/queries'
import { createBreakpoint, useDebounce } from 'react-use'
import { getCharacterDetails } from '../api/character'
import useBasePath from '../hooks/useBasePath'

export const useSubscription = (user?: wowaudit.User) => {
  const subscription = user?.subscription
  const declined = !!user?.linkedAccounts?.filter(
    (a) =>
      a.displayType === 'Patreon' && a.details?.status === 'declined_patron',
  ).length

  const legacySubscription = user?.linkedAccounts?.find(
    (a) =>
      a.displayType === 'Patreon' &&
      a.details?.active &&
      a.details.pledge_tier !== 'Free',
  )

  const subscriptionActive =
    (subscription?.active && !!subscription.tierName) ||
    !!legacySubscription?.details?.pledge_tier

  const subscriptionColor =
    premiumColors[
      subscription?.tierName ||
        legacySubscription?.details?.pledge_tier ||
        'Free'
    ]

  const subscriptionName =
    (subscription?.active
      ? subscription.tierName
      : legacySubscription?.details?.pledge_tier) || 'Free'

  return {
    declined,
    subscription,
    legacySubscription,
    subscriptionActive,
    subscriptionName,
    subscriptionColor,
  }
}

type ActionableEntity = {
  ids: string[]
  teamReference?: wowaudit.TeamReference
  text: string
  ctaPath?: string
  ctaText: string
  className?: string
}

export const useActionable = (user?: wowaudit.User) => {
  const { t } = useTranslation()

  const storedDismissedActions = JSON.parse(
    localStorage.getItem('dismissedActions') ?? '[]',
  )
  const [dismissedActions, setDismissedActions] = useState<string[]>(
    storedDismissedActions,
  )

  const dismissActionableEntity = (entity: ActionableEntity) => {
    const newDismissedActions = [...dismissedActions, ...entity.ids]
    setDismissedActions(newDismissedActions)
    localStorage.setItem(
      'dismissedActions',
      JSON.stringify(newDismissedActions),
    )
  }

  const actionableCharacters =
    user?.characterReferences?.filter(
      (ch) =>
        ch.actionable &&
        !dismissedActions.includes(`character_${ch.id}_${ch.gdprStatus}`),
    ) ?? []

  const actionableTeams =
    user?.teamReferences?.filter(
      (t) =>
        t.markedForDeletionAt &&
        !dismissedActions.includes(`team_${t.id}_${t.markedForDeletionAt}`),
    ) ?? []

  const { data: accessForTeams } = useUserTeamAccess(
    actionableTeams?.map((t) => t.id) ?? [],
  )

  const actionableTeamsWithAccess = actionableTeams.filter(
    (t) => accessForTeams?.[t.id.toString()],
  )

  const actionableEntities: ActionableEntity[] = [
    ...actionableTeamsWithAccess.map((entity) => ({
      ids: [`team_${entity.id}_${entity.markedForDeletionAt}`],
      teamReference: entity,
      text: t('profile.team-requires-action-warning', {
        name: entity.name,
        owner: entity.ownerReference.name,
      }),
      ctaPath:
        window.location.pathname === `${entity.path}/settings#team`
          ? undefined
          : `${entity.path}/settings#team`,
      ctaText: t('profile.team-settings'),
      className: 'bg-red-500',
    })),
    ...(actionableCharacters.length
      ? [
          {
            ids: actionableCharacters.map(
              (ch) => `character_${ch.id}_${ch.gdprStatus}`,
            ),
            text: t('profile.character-requires-action-warning'),
            ctaPath:
              window.location.pathname === '/profile/characters'
                ? undefined
                : '/profile/characters',
            ctaText: t('header.profile'),
            className: 'bg-yellow-500',
          },
        ]
      : []),
  ]

  const [currentActionIndex, setCurrentActionIndex] = useState<number>(0)
  const currentAction: ActionableEntity | undefined =
    actionableEntities[currentActionIndex]

  const hasNextAction = currentActionIndex < actionableEntities.length - 1
  const hasPreviousAction = currentActionIndex > 0

  return {
    actionableEntities,
    currentAction,
    currentActionIndex,
    setCurrentActionIndex,
    hasNextAction,
    hasPreviousAction,
    dismissActionableEntity,
  }
}

interface RefreshDataOptions {
  retriever: (
    id: number,
    params?: Record<string, string>,
  ) => Promise<{ job_id: string }>
  params?: Record<string, string>
  id?: number
  successKey?: string
  callback?: () => void
  queryToInvalidate?: QueryKey
}

export const useRefreshData = (options: RefreshDataOptions) => {
  const { t } = useTranslation()
  const { retriever, id, params, successKey, callback, queryToInvalidate } =
    options
  const queryClient = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const [updateTriggeredAt, setUpdateTriggeredAt] = useState<Date>()

  const refreshData = (customId?: number) => {
    setRefreshing(true)
    setUpdateTriggeredAt(new Date())

    retriever(customId ?? (id as number), params)
      .then(async (response) => {
        awaitPendingJob(response.job_id, (result) => {
          if (result.error) {
            displayErrorToast(result.error, t)
          } else {
            if (successKey) toast.success(t(successKey))
            if (callback) callback()
            if (queryToInvalidate) {
              queryClient.invalidateQueries({ queryKey: queryToInvalidate })
            }
          }
          setRefreshing(false)
        })
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setRefreshing(false)
      })
  }

  return {
    refreshData,
    refreshing,
    updateTriggeredAt,
  }
}

export const useRefreshCharacterDetails = (
  character: wowaudit.CharacterReference | undefined,
  options: RefreshDataOptions,
) => {
  const { t } = useTranslation()
  const { refreshing, refreshData, updateTriggeredAt } = useRefreshData(options)

  useEffect(() => {
    if (updateTriggeredAt && character?.refreshFailedAt) {
      if (dayjs(updateTriggeredAt).isBefore(dayjs(character.refreshFailedAt))) {
        toast.error(
          t(`errors.character_update_error`, {
            code: character.lastStatusCode,
          }),
        )
      }
    }
  }, [character?.refreshFailedAt])

  return { refreshing, refreshData, updateTriggeredAt }
}

export const imageUrl = (path: string) => {
  const assetHost = window.page_info.assetHost
  return `${assetHost}/images/${path}`
}

export const useLogo = () => {
  const { currentScheme } = useTheme()
  const Logo = currentScheme === 'dark' ? BnetLogoWhite : BnetLogoBlack

  return { Logo }
}

const getPreference = (
  teamReference: wowaudit.TeamReference,
  preferenceMap: Map<string, { order_in_menu?: number }>,
) => {
  const ownerKey = `owner_${teamReference.ownerReference.id}`
  const teamKey = `team_${teamReference.id}`
  const ownerPreference = preferenceMap.get(ownerKey)
  const teamPreference = preferenceMap.get(teamKey)

  return {
    hasPreference: Boolean(ownerPreference || teamPreference),
    order:
      teamPreference?.order_in_menu ?? ownerPreference?.order_in_menu ?? 99,
  }
}

export const useVisibleTeamsForUser = (user: wowaudit.User | undefined) => {
  if (!user) return []

  const visibleTeamPreferences = user.teamPreferences.filter(
    (teamPreference) => teamPreference.visibility === 'visible',
  )

  const preferenceMap = new Map(
    visibleTeamPreferences.map((preference) => {
      const key = preference.team_id
        ? `team_${preference.team_id}`
        : `owner_${preference.owner_id}`
      return [key, preference]
    }),
  )

  return user.teamReferences
    .map((teamReference) => ({
      teamReference,
      preference: getPreference(teamReference, preferenceMap),
    }))
    .filter(({ preference }) => preference.hasPreference)
    .sort((a, b) => a.preference.order - b.preference.order)
    .map(({ teamReference }) => teamReference)
}

export const useBlizzardApi = <TBlizzardEntity>(
  type: 'character' | 'guild',
  t: TFunction,
) => {
  const basePath = useBasePath(type)
  const queryClient = useQueryClient()
  const [_1, _2, uriRegion, uriRealm, uriName] =
    window.location.pathname.split('/')

  const [blizzardInfo, setBlizzardInfo] = useState<TBlizzardEntity>()
  const [blizzardStatus, setBlizzardStatus] = useState<
    '' | 'pending' | 'not-found' | 'found'
  >('')

  const searchBlizzard = () => {
    setBlizzardStatus('pending')
    getEntityFromBlizzard(uriName, uriRealm, uriRegion, type)
      .then((response) => {
        if (response.length) {
          // This is a hacky timeout to allow the background job to retrieve character data
          // which is triggered automatically upon entity creation
          setTimeout(() => {
            setBlizzardInfo(response[0])
            queryClient.invalidateQueries({
              queryKey: [type, { path: basePath }],
            })
            setBlizzardStatus('found')
          }, 3000)
        } else {
          setBlizzardStatus('not-found')
        }
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setBlizzardStatus('')
      })
  }

  return {
    uriRegion,
    uriRealm,
    uriName,
    blizzardInfo,
    blizzardStatus,
    searchBlizzard,
  }
}

export const useCharacterDetailsStore = <T>(
  getEntityId: (entity: T) => number | undefined,
) => {
  const [detailsStore, setDetailsStore] = useState<{
    [id: string]: wowaudit.CharacterDetails
  }>({})
  const [pendingRecords, setPendingRecords] = useState<T[]>([])

  const fetchPendingRecords = useCallback(
    async (newRecords?: T[], force = false) => {
      const recordsToFetch = newRecords ?? pendingRecords
      if (recordsToFetch.length === 0) return

      const missingIds = recordsToFetch
        .map((entity) => getEntityId(entity))
        .filter((id) => id && (!detailsStore[id] || force)) as number[]

      if (missingIds.length === 0) return

      const newDetails = await getCharacterDetails(missingIds)

      const newDetailsMap = newDetails.reduce((acc, details) => {
        acc[details.id] = details
        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>)

      setDetailsStore((prev) => ({ ...prev, ...newDetailsMap }))
    },
    [pendingRecords, detailsStore, getEntityId],
  )

  useDebounce(fetchPendingRecords, 300, [
    pendingRecords,
    detailsStore,
    getEntityId,
  ])

  const fetchNewDetails = (newRecords: T[]) => {
    setPendingRecords(newRecords)
  }

  const immediatelyFetchNewDetails = (newRecords: T[], force = false) => {
    fetchPendingRecords(newRecords, force)
  }

  return {
    detailsStore,
    fetchNewDetails,
    immediatelyFetchNewDetails,
  }
}

// Improved version of https://usehooks.com/useclickaway/
export const useClickOutside = (
  ref: React.RefObject<HTMLElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    let startedInside = false
    let startedWhenMounted = false

    const listener = (event: MouseEvent | TouchEvent) => {
      // Do nothing if `mousedown` or `touchstart` started inside ref element
      if (startedInside || !startedWhenMounted) return
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target as Node)) return

      handler(event)
    }

    const validateEventStart = (event: MouseEvent | TouchEvent) => {
      startedWhenMounted = Boolean(ref.current)
      startedInside = Boolean(
        ref.current && ref.current.contains(event.target as Node),
      )
    }

    document.addEventListener('mousedown', validateEventStart)
    document.addEventListener('touchstart', validateEventStart)
    document.addEventListener('click', listener)

    return () => {
      document.removeEventListener('mousedown', validateEventStart)
      document.removeEventListener('touchstart', validateEventStart)
      document.removeEventListener('click', listener)
    }
  }, [ref, handler])
}

export const useCopyToClipboard = (criteria?: (text: string) => boolean) => {
  const [copied, setCopied] = useState<string>()

  const copyToClipboard = (text: string) => {
    if (criteria && !criteria(text)) return

    navigator.clipboard.writeText(text)
    setCopied(text)

    window.getSelection()?.removeAllRanges()

    setTimeout(() => {
      setCopied(undefined)
    }, 3000)
  }

  return {
    copied,
    copyToClipboard,
  }
}

// These are Tailwind's default breakpoints
export const useBreakpoint = createBreakpoint({
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
})
